import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

export interface AppPageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
  className?: string;
  noMotion?: boolean;
  noBorder?: boolean;
  align?: 'baseline' | 'start';
}

export function AppPageHeader({
  title,
  subtitle,
  icon,
  actions,
  eyebrow,
  className,
  noMotion,
  noBorder,
  align = 'baseline',
}: AppPageHeaderProps) {
  const wrapperClassName = cn(
    'flex flex-col gap-3 pb-4 sm:flex-row sm:items-end sm:justify-between sm:gap-4',
    !noBorder && 'border-b border-border/40',
    className,
  );

  const inner = (
    <>
      <div className={cn('min-w-0 flex-1 flex gap-3', align === 'baseline' ? 'items-end' : 'items-start')}>
        {icon && (
          <div className="text-muted-foreground shrink-0 pb-1" aria-hidden>
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-1.5">
              {eyebrow}
            </div>
          )}
          <h1 className="font-sans text-2xl md:text-3xl font-semibold tracking-tight text-foreground leading-tight truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex items-center gap-2 sm:justify-end shrink-0 flex-wrap">
          {actions}
        </div>
      )}
    </>
  );

  if (noMotion) {
    return <div className={wrapperClassName}>{inner}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={wrapperClassName}
    >
      {inner}
    </motion.div>
  );
}

export default AppPageHeader;
